// ============================================================================
// Sources — MS-13
// ----------------------------------------------------------------------------
// Página de Fontes de Conhecimento agora com duas tabs:
//   • Conexões → fontes RSS/API/Website/Custom (comportamento original).
//   • Arquivos → upload PDF/TXT/MD com ingestão semântica.
// ============================================================================

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sourcesApi, groupsApi } from "@/services/api";
import type { ExternalSource, SourceType } from "@/types/syra";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Plus, TestTube, Rss, Globe, Code, Plug, ChevronLeft, ChevronRight, Link2, FileText } from "lucide-react";
import SourceFilesTab from "@/components/sources/SourceFilesTab";

const typeIcons: Record<SourceType, React.ReactNode> = {
  rss: <Rss className="w-4 h-4" />,
  api: <Code className="w-4 h-4" />,
  website: <Globe className="w-4 h-4" />,
  custom: <Plug className="w-4 h-4" />,
};

const PAGE_SIZE = 20;
const VALID_TABS = ["conexoes", "arquivos"] as const;
type Tab = (typeof VALID_TABS)[number];

export default function Sources() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: Tab = (VALID_TABS as readonly string[]).includes(raw ?? "") ? (raw as Tab) : "conexoes";
  const setTab = (t: string) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Fontes de Conhecimento</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Conecte fontes externas e envie arquivos que a IA consulta para enriquecer respostas.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger value="conexoes" className="gap-2"><Link2 className="w-4 h-4" />Conexões</TabsTrigger>
          <TabsTrigger value="arquivos" className="gap-2"><FileText className="w-4 h-4" />Arquivos</TabsTrigger>
        </TabsList>

        <TabsContent value="conexoes" className="mt-4">
          <ConexoesTab />
        </TabsContent>
        <TabsContent value="arquivos" className="mt-4">
          <SourceFilesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConexoesTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["sources", page],
    queryFn: () => sourcesApi.list({ page, limit: PAGE_SIZE }),
  });

  const sources = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const deleteMut = useMutation({
    mutationFn: sourcesApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sources"] }); toast({ title: "Fonte removida" }); },
  });
  const testMut = useMutation({
    mutationFn: sourcesApi.test,
    onSuccess: (res) => toast({ title: res.success ? "Conexão OK" : "Falha", description: res.message }),
  });
  const createMut = useMutation({
    mutationFn: sourcesApi.create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sources"] }); setShowForm(false); toast({ title: "Fonte criada" }); },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={() => setShowForm(true)}><Plus className="w-4 h-4 mr-1" /> Nova Fonte</Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </CardContent></Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Grupo</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Última Consulta</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{typeIcons[s.type as SourceType]}</TableCell>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-muted-foreground">{s.group_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{s.url}</TableCell>
                      <TableCell>
                        <Badge variant={s.active ? "default" : "secondary"}>{s.active ? "Ativa" : "Inativa"}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.last_fetched ? new Date(s.last_fetched).toLocaleDateString("pt-BR") : "-"}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="icon" onClick={() => testMut.mutate(s.id)} disabled={testMut.isPending}>
                          <TestTube className="w-4 h-4" />
                        </Button>
                        <ConfirmDeleteDialog itemName={s.name} itemType="fonte" onConfirm={() => deleteMut.mutate(s.id)} disabled={deleteMut.isPending} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {sources.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhuma fonte cadastrada</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">Página {page} de {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova Fonte Externa</DialogTitle></DialogHeader>
          <SourceForm onSubmit={(d) => createMut.mutate(d)} loading={createMut.isPending} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SourceForm({ onSubmit, loading }: { onSubmit: (d: Omit<ExternalSource, "id" | "created_at">) => void; loading: boolean }) {
  const [form, setForm] = useState({ name: "", type: "rss" as SourceType, url: "", group_id: "", group_name: "", categories: [] as string[], active: true });
  const [catInput, setCatInput] = useState("");
  const { data: groups = [] } = useQuery({ queryKey: ["groups"], queryFn: groupsApi.list });

  const addCategory = () => {
    if (catInput.trim()) { setForm({ ...form, categories: [...form.categories, catInput.trim()] }); setCatInput(""); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Nome</label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Tipo</label>
        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as SourceType })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="rss">RSS</SelectItem>
            <SelectItem value="api">API</SelectItem>
            <SelectItem value="website">Website</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">URL</label>
        <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Grupo</label>
        <Select value={form.group_id} onValueChange={(v) => {
          const g = groups.find((g) => g.id === v);
          setForm({ ...form, group_id: v, group_name: g?.name ?? "" });
        }}>
          <SelectTrigger><SelectValue placeholder="Selecione um grupo" /></SelectTrigger>
          <SelectContent>
            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Categorias</label>
        <div className="flex gap-2">
          <Input value={catInput} onChange={(e) => setCatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCategory())} placeholder="Adicionar..." />
          <Button type="button" variant="outline" onClick={addCategory}>+</Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {form.categories.map((c, i) => (
            <Badge key={i} variant="secondary" className="text-xs cursor-pointer" onClick={() => setForm({ ...form, categories: form.categories.filter((_, j) => j !== i) })}>
              {c} ×
            </Badge>
          ))}
        </div>
      </div>
      <Button onClick={() => onSubmit(form as Omit<ExternalSource, "id" | "created_at">)} disabled={loading || !form.name || !form.url || !form.group_id} className="w-full">
        {loading ? "Criando..." : "Criar Fonte"}
      </Button>
    </div>
  );
}
